import React from 'react'
import clsx from 'clsx'
import { useTheme } from '@mui/material'
import { useDrag } from 'react-dnd'
import { AnimatePresence, motion } from 'framer-motion'

import { Hierarchy3, Rank } from 'iconsax-react'

import Tooltip from '@SonarCX/components/atoms/Tooltip'
import Subtasks from '../Subtasks'
import Comments from '../Comments'
import Title from './Title'
import Details from './Details'
import Card from '@SonarCX/components/atoms/Card'

import { TASK_CARD_ITEM_TYPE } from '../Column'
import { useTaskPageState } from '@SonarCX/providers/TaskPageStateProvider'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import {
  useTaskCardState,
  TabState
} from '@SonarCX/providers/TaskCardStateProvider'
import useFavorites from '@SonarCX/hooks/useFavorites'
import useStyles from './styles'

type TaskCardProps = {
  task: LocalTask
  onClick?: () => void
  containerID: string
  hideDragHandle?: boolean
}

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onClick,
  containerID,
  hideDragHandle = false
}) => {
  const theme = useTheme()

  const { favoriteTasks } = useFavorites()
  const { lastViewedCard, setLastViewedCard } = useTaskPageState()
  const { tasks, loadTask } = useTaskAPI()
  const { tabState } = useTaskCardState()

  const isFavorite = React.useMemo(() => {
    if (!task.id) return false

    return favoriteTasks.some(fav => fav.item_id === task.id)
  }, [favoriteTasks, task])

  const [, dragRef, dragPreviewRef] = useDrag({
    item: { type: TASK_CARD_ITEM_TYPE, task, containerID }
  })

  const [glowing, setGlowing] = React.useState(false)

  const tab = React.useMemo(
    () => tabState[task.id] ?? TabState.NONE,
    [tabState, task.id]
  )

  const parentName = React.useMemo(() => {
    if (!task.parent_id) {
      return ''
    }

    return tasks[task.parent_id]?.title ?? ''
  }, [task.parent_id, tasks])

  const cardRef = React.useRef<HTMLDivElement>()

  const classes = useStyles()

  const parentIcon = React.useMemo(
    () =>
      task.parent_id ? (
        <Tooltip title={`Subtask of ${parentName}`} arrow>
          <Hierarchy3 size={theme.size.lg} />
        </Tooltip>
      ) : task.sub_tasks > 0 ? (
        <Tooltip title='Parent' arrow>
          <Rank size={theme.size.lg} />
        </Tooltip>
      ) : null,
    [task.parent_id, task.sub_tasks, parentName, theme.size.lg]
  )

  // Load parent task if needed
  React.useEffect(() => {
    const parentID = task.parent_id
    if (!(parentID in tasks)) {
      loadTask(parentID)
    }
  }, [task.parent_id, tasks, loadTask])

  // Scroll to last viewed task
  React.useEffect(() => {
    if (lastViewedCard === task.id) {
      if (cardRef.current) {
        setGlowing(true)
        setTimeout(() => {
          setGlowing(false)
        }, 5000)

        cardRef.current.scrollIntoView({
          // @ts-ignore
          behavior: 'instant',
          block: 'nearest'
        })
        setLastViewedCard(null)
      }
    }
  }, [lastViewedCard, setLastViewedCard, task.id])

  return (
    <Card
      ref={el => {
        cardRef.current = el
        dragPreviewRef(el)
      }}
      onClick={() => onClick && onClick()}
      className={clsx(classes.root, glowing && classes.glowRoot)}
    >
      <Title task={task} isFavorite={isFavorite} parentIcon={parentIcon} />

      <Details task={task} dragRef={dragRef} hideDragHandle={hideDragHandle} />

      <AnimatePresence mode='wait'>
        {tab !== TabState.NONE && (
          <motion.div
            key={tab}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={classes.extraContent}
          >
            {tab === TabState.SUBTASKS && (
              <Subtasks task={task} containerID={containerID} />
            )}

            {tab === TabState.COMMENTS && <Comments task={task} />}
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  )
}

export default TaskCard
